/*
 * Copyright (c) 2023 SmartWifi - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential. No warranty, explicit or implicit provided. In
 * no event will the author be liable for any claim or damages.
 */

import { Button, Label, Nav, NavItem, Tooltip } from "reactstrap";
import { useLocation } from "react-router-dom";
import { useContext, useState } from "react";
import { AccountContext, ContextUpdater } from "../context/ContextProvider";
import {
  requestAdminDashboardData,
  requestAllAccounts,
  requestAllCaptivePortalLayouts,
  requestAllRouterStatuses,
  requestBrandingAdminGetAll,
  requestClient,
  requestClientInfoAndTemplate,
  requestClientUsers,
  requestMarketingAllTemplates,
  requestMarketingHomeInfo,
  requestMarketingIntegrationInfo,
  requestPayPalSubscriptionDiscounts
} from "../context/RequestManager";
import ModalItemList from "../pages/admin/ModalItemList";
import useUpdateState from "../util/useUpdateState";

import adminDashboard from "../assets/images/admin_dashboard_color.png";
import adminRouter from "../assets/images/admin_router2_color.png";
import adminUsers from "../assets/images/admin_users_color.png";
import adminTemplates from "../assets/images/admin_templates_color.png";
import adminMarketing from "../assets/images/admin_marketing_color.png";
import adminProvision from "../assets/images/admin_provision_color.png";
import adminBilling from "../assets/images/admin_billing_color.png"
import adminBranding from "../assets/images/white_label.png";
import userDashboard from "../assets/images/user_dashboard_color.png";
import userGuests from "../assets/images/user_guests_color.png";
import userMarketing from "../assets/images/user_marketing_color.png";
import userIntegrations from "../assets/images/user_integrations.png";
import userLogin from "../assets/images/user_login_page_color.png";
import userSettings from "../assets/images/user_business_info.png";
import wifiSettings from "../assets/images/user_wifi_settings.png";
import { UserListInitialSize } from "../pages/search/UserSearch";
import { isCustomDomain } from "../util/CustomDomain";

const Sidebar = () => {
  // contexts
  const updateContexts = useContext(ContextUpdater);
  const accountContext = useContext(AccountContext);

  // state
  const [collapsed, setCollapsed] = useState(true);
  const [tooltip, setTooltip] = useState(null);

  const initialClientModalState = { open: false, option: null };
  const [clientModalState, , updateClientModalState] = useUpdateState(initialClientModalState);

  const initialBrandModalState = { open: false, option: null };
  const [brandModalState, , updateBrandModalState] = useUpdateState(initialBrandModalState);

  const location = useLocation();
  const currentRoute = location.pathname;

  // helper variables
  const isSubAdmin = accountContext.subAdmin ?? false;
  const isAdmin = (accountContext.admin ?? false) || isSubAdmin;
  const provisionBusinessPermission = accountContext.permissions.provisionBusiness;
  const clientAvailable = accountContext.clientId != null;
  const clientReqDisabled = isAdmin && !clientAvailable;
  const provisionBusinessEnabled = (isAdmin && !isSubAdmin) || (isSubAdmin && (provisionBusinessPermission || isCustomDomain()));
  
  const domainConfigured = accountContext.domainBrand?.configurationComplete ?? true;

  const navigation = [
    {
      title: "Admin",
      separator: true,
      disabled: false,
      visible: isAdmin,
    },
    {
      title: "Admin Dashboard",
      image: adminDashboard,
      href: "/admin",
      visible: isAdmin,
      handler: () => {
        requestAdminDashboardData({
          context: updateContexts,
          token: accountContext.token,
          navigate: (_) => "/admin"
        });
      }
    },
    {
      title: "Router Status",
      image: adminRouter,
      href: "/router-status",
      visible: isAdmin,
      handler: () => {
        requestAllRouterStatuses({
          context: updateContexts,
          token: accountContext.token,
          clearSelectedRouter: true,
          navigate: (_) => "/router-status"
        });
      }
    },
    {
      title: "Dashboard Users",
      image: adminUsers,
      href: "/dashboard-users",
      visible: isAdmin,
      handler: () => {
        requestAllAccounts({
          context: updateContexts,
          token: accountContext.token,
          navigate: (_) => "/dashboard-users"
        });
      }
    },
    {
      title: "Login Page Layouts",
      image: adminTemplates,
      href: "/login-pages",
      visible: isAdmin && !isSubAdmin,
      handler: () => {
        requestAllCaptivePortalLayouts({
          context: updateContexts,
          token: accountContext.token,
          includeName: true,
          includeConfig: true,
          navigate: (_) => "/login-pages"
        });
      }
    },
    {
      title: "Marketing Templates",
      image: adminMarketing,
      href: "/marketing-templates",
      visible: isAdmin && !isSubAdmin,
      handler: () => {
        requestMarketingAllTemplates({
          context: updateContexts,
          token: accountContext.token,
          isAdmin: isAdmin,
          includeSenders: true,
          navigate: (_) => "/marketing-templates"
        });
      }
    },
    {
      title: "Admin Billing",
      image: adminBilling,
      href: "/admin-billing",
      visible: isAdmin && !isSubAdmin,
      handler: () => {
        requestPayPalSubscriptionDiscounts({
          context: updateContexts,
          token: accountContext.token,
          navigate: (_) => "/admin-billing"
        });
      }
    },
    {
      title: "Provision Business",
      image: adminProvision,
      href: "/provision",
      visible: provisionBusinessEnabled,
      handler: () => {
        requestAllCaptivePortalLayouts({
          context: updateContexts,
          token: accountContext.token,
          includeImages: false,
          contextKey: "provisioning",
          navigate: (_) => "/provision"
        })
      }
    },
    {
      title: "White Label Brands",
      image: adminBranding,
      href: "/admin-branding",
      subHrefs: ["/brand-settings"],
      visible: isAdmin,
      handler: () => {
        requestBrandingAdminGetAll({
          context: updateContexts,
          token: accountContext.token,
          navigate: (_) => "/admin-branding"
        });
      }
    },
    {
      title: accountContext.clientSummary?.name ?? accountContext.clientInfo?.name ?? "No Business Selected",
      separator: true,
      disabled: clientReqDisabled,
      visible: true
    },
    {
      title: "Dashboard",
      image: userDashboard,
      href: "/dashboard",
      disabled: clientReqDisabled,
      handler: (clientId) => {
        requestClientInfoAndTemplate({
          context: updateContexts,
          token: accountContext.token,
          clientId: clientId,
          navigate: (_) => "/dashboard"
        })
      }
    },
    {
      title: "Guests",
      image: userGuests,
      href: "/guests",
      disabled: clientReqDisabled,
      handler: (clientId) => {
        requestClientUsers({
          context: updateContexts,
          token: accountContext.token,
          clientId: clientId,
          limit: currentRoute === "/guests" ? null : UserListInitialSize,  // If already on guest page, fetch all
          navigate: (_) => "/guests"
        })
      }
    },
    {
      title: "Login Page",
      image: userLogin,
      href: "/login-page",
      disabled: clientReqDisabled,
      handler: (clientId) => {
        requestClient({
          context: updateContexts,
          token: accountContext.token,
          clientId: clientId,
          navigate: (_) => "/login-page"
        })
      }
    },
    {
      title: "Marketing",
      image: userMarketing,
      href: "/marketing",
      disabled: clientReqDisabled,
      handler: (clientId) => {
        requestMarketingHomeInfo({
          context: updateContexts,
          token: accountContext.token,
          clientId: clientId,
          navigate: (_) => "/marketing"
        })
      }
    },
    {
      title: "Integrations",
      image: userIntegrations,
      href: "/integrations",
      disabled: clientReqDisabled,
      handler: (clientId) => {
        requestMarketingIntegrationInfo({
          context: updateContexts,
          token: accountContext.token,
          clientId: clientId,
          navigate: (_) => "/integrations"
        });
      }
    },
    {
      title: "WiFi Settings",
      image: wifiSettings,
      imageHeight: "auto",
      href: "/wifi-settings",
      disabled: clientReqDisabled,
      handler: (clientId) => requestClient({
        context: updateContexts,
        token: accountContext.token,
        clientId: clientId,
        settingsPage: "",
        navigate: (_) => "/wifi-settings"
      })
    },
    {
      title: "Business Info",
      image: userSettings,
      href: "/business-settings",
      disabled: clientReqDisabled,
      handler: (clientId) => requestClient({
        context: updateContexts,
        token: accountContext.token,
        clientId: clientId,
        settingsPage: "",
        navigate: (_) => "/business-settings"
      })
    },
  ];
  
  // do not show if not logged in
  if (!accountContext.token) {
    return null;
  }

  // Routes that should not be scaled for mobile
  const excludeRoutes = [
    "/captiveportal",
    "/captiveportalpreview",
  ];

  const shouldScale = !excludeRoutes.includes(location.pathname);

  const textStyle = {
    opacity: collapsed ? "0%" : "100%",
    width: collapsed ? "0px" : "",
    transition: "all 350ms",
  };

  // handlers
  const openClientModal = (option) => {
    updateClientModalState({ open: true, option: option });
  };

  const openBrandModal = (option) => {
    updateBrandModalState({ open: true, option: option })
  }

  const handleClientSelected = (client) => {  
    // select client
    const { id, name } = client;
    accountContext.updateState({
      clientId: id,
      clientSummary: { id, name }
    });

    // call option on click
    const handler = navigation.find(navi => navi.title === clientModalState.option)?.handler;
    handler?.(id);
  };

  const handleBrandSelected = (brand) => {  
    const { id } = brand;

    // select client
    accountContext.updateState({
      brand: brand,
    });

    // call option on click
    const handler = navigation.find(navi => navi.title === brandModalState.option)?.handler;
    handler?.(id);
  };

  return (
    <aside className={`sidebar-area shadow mobile-container-scale ${shouldScale ? "mobile-container-sidebar-scale" : ""}`} style={{ width: collapsed ? "81px" : "240px" }}>
      <div className="py-3 px-2 no-wrap">
        <div className="mb-2 d-flex justify-content-center">
          <Button className="fill-container" outline size="sm" onClick={() => setCollapsed(!collapsed)}>
            <i className={`icon bi bi-chevron-${collapsed ? "right" : "left"}`} />
          </Button>
        </div>
        <div className="mt-2">
          <Nav vertical className="sidebarNav">
            {navigation.map((navi, index) => {
              if (!(navi.visible ?? true)) {
                return null;
              }

              if (navi.separator) {
                return (
                  <div key={index} className={`my-2 separator ${navi.disabled ? "text-muted" : ""}`} style={{height: "20px"}}>
                    {!collapsed && <span>{navi.title}</span>}
                  </div>
                );
              }

              const subHrefs = navi.subHrefs ?? []
              const tooltipId = `tooltip-sidebar-${index}`;
              const selected = currentRoute === navi.href || subHrefs.includes(currentRoute);
              const canNavigate = !isCustomDomain() || domainConfigured || selected;
              const handler = canNavigate ? (navi.disabled
                ? () => openClientModal(navi.title)
                : () => navi.handler(accountContext.clientId)) : null;

              return (
                <NavItem
                  key={index}
                  id={tooltipId}
                  className={`sidenav-bg ${canNavigate ? "clickable" : ""}  `}
                  onClick={handler}
                  style={selected ? {
                    backgroundColor: "#26F3",
                    border: "1px solid #26F4",
                    borderRadius: "30px"
                  } : {
                    border: "1px solid #0000"
                  }}
                >
                  <Label
                    className={`d-flex align-items-center ps-3 mt-2 no-wrap ${selected ? "text-primary" : ""}`}
                    style={{ opacity: !canNavigate || navi.disabled ? "50%" : "100%" }}
                  >
                    {navi.image && <img src={navi.image} alt="" style={{ width: navi.imageWidth ?? "30px", height: navi.imageHeight ?? "30px" }} />}
                    {navi.icon && <i className={navi.icon} style={{ fontSize: "1.1rem" }} />}
                    <span className="ms-3 d-inline-block sidebarText" style={textStyle}>
                      {navi.title}
                    </span>
                  </Label>
                  <Tooltip
                    placement="right"
                    isOpen={tooltipId === tooltip}
                    target={tooltipId}
                    toggle={() => setTooltip(!collapsed || tooltipId === tooltip ? null : tooltipId)}
                  >
                    {navi.title}
                  </Tooltip>
                </NavItem>
              )
            })}
          </Nav>
        </div>
      </div>
      <ModalItemList
        type="client"
        isOpen={isAdmin && clientModalState.open}
        items={accountContext.adminClientList}
        onClick={handleClientSelected}
        onDone={() => updateClientModalState({ open: false })}
      />
      <ModalItemList
        type="brand"
        isOpen={isAdmin && brandModalState.open}
        items={[]}
        onClick={handleBrandSelected}
        onDone={() => updateBrandModalState({ open: false })}
      />
    </aside>
  );
};

export default Sidebar;
